import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Clock, LifeBuoy } from "lucide-react";
import PageContainer from "@/components/PageContainer";
import ResetPlayer from "@/components/ResetPlayer";
import {
  RESET_STATES,
  FOCUS_TO_STATES,
  getInterventionsForState,
  getInterventionById
} from "@/lib/resetData";
import { getFocusAreas } from "@/lib/storage";
import { usePremium } from "@/lib/premium";
import { track } from "@/lib/analytics";

const orderStates = (focus) => {
  if (!focus?.length) return RESET_STATES;
  const priority = new Set();
  focus.forEach((f) => (FOCUS_TO_STATES[f] || []).forEach((s) => priority.add(s)));
  return [
    ...RESET_STATES.filter((s) => priority.has(s.id)),
    ...RESET_STATES.filter((s) => !priority.has(s.id)),
  ];
};

export default function Reset() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { premium, loading: premiumLoading } = usePremium();
  const stateId = params.get("state");
  const playId = params.get("play");
  const [active, setActive] = useState(null);

  // ?play deep-links (e.g. Toolkit) respect entitlements without turning an
  // immediate support moment into an upgrade prompt.
  useEffect(() => {
    if (!playId || premiumLoading) return;
    const r = getInterventionById(playId);
    if (!r) return;
    if (!r.free && !premium) {
      const fallbackState = r.states?.[0] || "reset";
      toast("That saved technique is not available on this plan.", {
        description: "Here are free reset options instead.",
      });
      setParams({ state: fallbackState }, { replace: true });
      return;
    }
    setActive(r);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playId, premium, premiumLoading, setParams]);

  const focus = useMemo(() => getFocusAreas(), []);
  const states = useMemo(() => orderStates(focus), [focus]);
  const state = RESET_STATES.find((s) => s.id === stateId) || null;
  const interventions = useMemo(
    () => (stateId ? getInterventionsForState(stateId) : []),
    [stateId]
  );
  const visibleInterventions = useMemo(
    () => (premium ? interventions : interventions.filter((item) => item.free)),
    [interventions, premium]
  );

  const openIntervention = (r) => {
    if (!r.free && !premium) {
      toast("That technique is not available on this plan.", {
        description: "Choose any of the free reset options here.",
      });
      return;
    }
    track("exercise_selected", { state: stateId, intervention: r.id });
    setActive(r);
  };

  return (
    <PageContainer testid="reset-page" className="pt-8">
      <AnimatePresence>
        {active && (
          <ResetPlayer
            intervention={active}
            state={stateId}
            premium={premium}
            onExit={() => {
              setActive(null);
              if (playId) setParams({}, { replace: true });
            }}
          />
        )}
      </AnimatePresence>

      {!state ? (
        <>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <h1
              className="text-[30px] leading-tight"
              style={{ color: "var(--un-text-1)", fontFamily: "'Lora', serif", fontWeight: 500 }}
            >
              What do you need help
              <br />
              <span
                className="italic"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: "var(--un-burgundy)", fontWeight: 400 }}
              >
                with right now?
              </span>
            </h1>
            <p className="mt-3 text-[14px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>
              Pick what's closest. A short, guided reset is a tap away — most take 1–5 minutes.
            </p>
          </motion.div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            {states.map((s, i) => (
              <motion.button
                key={s.id}
                data-testid={`reset-state-${s.id}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.04 * i }}
                onClick={() => setParams({ state: s.id })}
                className="un-press p-4 text-left rounded-3xl"
                style={{
                  background: "var(--un-bg-2)",
                  boxShadow: "0 4px 16px rgba(74,45,72,0.07)",
                }}
              >
                <p
                  className="text-[14px] font-medium leading-snug"
                  style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}
                >
                  {s.label}
                </p>
                <p className="text-[11px] mt-1 leading-snug" style={{ color: "var(--un-text-3)" }}>
                  {s.sub}
                </p>
              </motion.button>
            ))}
          </div>

          <button
            data-testid="reset-safereach-link"
            onClick={() => nav("/safereach")}
            className="mt-7 mb-10 w-full un-press p-4 rounded-3xl flex items-center gap-3 text-left"
            style={{ background: "rgba(62,92,80,0.10)" }}
          >
            <LifeBuoy size={18} style={{ color: "var(--un-safe)" }} />
            <div>
              <p className="text-[13px] font-medium" style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}>
                In crisis, or need a person?
              </p>
              <p className="text-[11px]" style={{ color: "var(--un-text-3)" }}>
                Safe Reach — independent support resources, any time.
              </p>
            </div>
          </button>
        </>
      ) : (
        <>
          <button
            data-testid="reset-back-btn"
            onClick={() => setParams({})}
            className="inline-flex items-center gap-1.5 text-[13px] un-press mb-5"
            style={{ color: "var(--un-text-3)", fontFamily: "Manrope, sans-serif" }}
          >
            <ArrowLeft size={14} /> All moments
          </button>

          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <h1
              className="text-[28px] leading-tight"
              style={{ color: "var(--un-text-1)", fontFamily: "'Lora', serif", fontWeight: 500 }}
            >
              {state.label}
            </h1>
            <p className="mt-2 text-[14px]" style={{ color: "var(--un-text-3)" }}>
              These help most here. Start with whichever feels doable.
            </p>
          </motion.div>

          <div className="mt-5 flex flex-col gap-3 mb-10">
            {visibleInterventions.map((r, i) => {
              return (
                <motion.button
                  key={r.id}
                  data-testid={`reset-intervention-${r.id}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => openIntervention(r)}
                  className="un-press p-4 text-left rounded-3xl flex items-center gap-4"
                  style={{
                    background: "var(--un-bg-2)",
                    boxShadow: "0 4px 16px rgba(74,45,72,0.06)",
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-[15px] font-medium"
                      style={{ color: "var(--un-text-1)", fontFamily: "Manrope, sans-serif" }}
                    >
                      {r.title}
                    </p>
                    <p
                      className="text-[13px] italic mt-0.5"
                      style={{ color: "var(--un-text-3)", fontFamily: "'Cormorant Garamond', serif", fontSize: "14px" }}
                    >
                      {r.tagline}
                    </p>
                  </div>
                  <span
                    className="shrink-0 inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full"
                    style={{
                      background: "rgba(196,161,106,0.16)",
                      color: "var(--un-text-2)",
                      fontFamily: "Manrope, sans-serif",
                    }}
                  >
                    <Clock size={11} />
                    {r.minutes} min
                  </span>
                </motion.button>
              );
            })}


          </div>
        </>
      )}
    </PageContainer>
  );
}
